import { formatPropertyAddress, formatStreetAddress } from '../utils/propertyAddress';
import { FleetLandingHeader, FleetLandingDock } from './FleetLandingNavigation';
import LandingNavIcon from './LandingNavIcon';
import { propertyFetch } from '../services/idxPropertyClient';
import { submitChatLead } from '../services/chatLeadService';
// src/components/LandingPage.js
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Logo from '../assets/logo.png';  
import ActionButtons from './ActionButtons';
import BlogsPage from './BlogsPage';
import ReviewsPage from './ReviewsPage';
import SEOHelmet from './SEOHelmet';
import LanguageSwitcher from './LanguageSwitcher';
import SEOLinks from './SEOLinks';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './LandingPage.css';
import './IntroVideo.css';
import HoneypotField, { isHoneypotSubmission } from './HoneypotField';

import './FleetLandingNavigation.css';

const LandingPage = ({ directListing = false, directSearch = false }) => {
  const { listingKey } = useParams();
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState(null);
  const [showMapPage, setShowMapPage] = useState(false);
  const [showGlobeMap, setShowGlobeMap] = useState(directSearch || directListing);
  const [, setShowGlobeMapResults] = useState(false);
  const [showBlogs, setShowBlogs] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showSignInDropdown, setShowSignInDropdown] = useState(false);
  const [signInUsername, setSignInUsername] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [registerData, setRegisterData] = useState({
    username: '',
    email: '',
    password: '',
    phoneNumber: '',
    name: ''
  });
  const [showIntroVideo, setShowIntroVideo] = useState(!directListing);
  const [showMLSDisclosure, setShowMLSDisclosure] = useState(false);
  const [scrollTransition, setScrollTransition] = useState(0);
  const [isOverReviewCard, setIsOverReviewCard] = useState(false);
  const reviewCarouselRef = React.useRef(null);
  const videoRef = React.useRef(null);
  const [showImagePageText, setShowImagePageText] = useState(false);
  const [showEPage, setShowEPage] = useState(false);
  const [showCityTiles, setShowCityTiles] = useState(false);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [currentPropertyIndex, setCurrentPropertyIndex] = useState(0);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectForm, setConnectForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    postalCode: '',
    smsConsent: false,
    serviceReq: 'buying'
  });
  const [submittingConnect, setSubmittingConnect] = useState(false);
  const [showConnectSuccess, setShowConnectSuccess] = useState(false);
  const [showHomeValueModal, setShowHomeValueModal] = useState(false);
  const [homeValueForm, setHomeValueForm] = useState({ name: '', email: '', phoneNumber: '', postalCode: '', smsConsent: false });
  const [submittingHomeValue, setSubmittingHomeValue] = useState(false);

  React.useEffect(() => {
    if (showIntroVideo && !showGlobeMap) {
      document.body.classList.add('intro-active');
    } else {
      document.body.classList.remove('intro-active');
    }
  }, [showIntroVideo, showGlobeMap]);

  // Ensure video plays after hydration/mount
  React.useEffect(() => {
    const v = videoRef.current;
    if (v && showIntroVideo) {
      v.load();
      v.play().catch(() => {});
    }
  }, [showIntroVideo]);

  React.useEffect(() => {
    const handleReviewScroll = () => {
      if (!reviewCarouselRef.current) return;

      const container = reviewCarouselRef.current;
      const cards = container.querySelectorAll('.review-card');
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.top + containerRect.height / 2;

      cards.forEach(card => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.top + cardRect.height / 2;
        const distance = Math.abs(containerCenter - cardCenter);
        const maxDistance = containerRect.height / 2;

        const opacity = Math.max(0.3, 1 - (distance / maxDistance) * 0.7);
        const scale = Math.max(0.9, 1 - (distance / maxDistance) * 0.1);

        card.style.opacity = opacity;
        card.style.transform = `scale(${scale})`;
      });
    };

    const carousel = reviewCarouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', handleReviewScroll);
      handleReviewScroll();
    }

    return () => {
      if (carousel) {
        carousel.removeEventListener('scroll', handleReviewScroll);
      }
    };
  }, [showIntroVideo]);

  React.useEffect(() => {
    // Hide intro video when on a direct listing route
    if (directListing && listingKey) {
      setShowIntroVideo(false);
      setShowGlobeMap(true);
    }
  }, [directListing, listingKey]);

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const viewportHeight = window.innerHeight;

      if (showIntroVideo && !showGlobeMap) {
        if (scrollTop >= viewportHeight * 0.5 && !showImagePageText) {
          setShowImagePageText(true);
        } else if (scrollTop < viewportHeight * 0.5 && showImagePageText) {
          setShowImagePageText(false);
        }

        if (scrollTop >= viewportHeight * 1.5 && !showEPage) {
          setShowEPage(true);
        } else if (scrollTop < viewportHeight * 1.5 && showEPage) {
          setShowEPage(false);
        }

        if (scrollTop >= viewportHeight * 2.5 && !showCityTiles) {
          setShowCityTiles(true);
        } else if (scrollTop < viewportHeight * 2.5 && showCityTiles) {
          setShowCityTiles(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showIntroVideo, showGlobeMap, showImagePageText, showEPage, showCityTiles]);

  React.useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  React.useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setIsAdmin(userData.roles?.includes('ROLE_ADMIN') || false);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
      }
    }

    // Fetch featured properties
    const fetchFeaturedProperties = async () => {
      try {
        const [mlsResponse, privateResponse, fallbackResponse] = await Promise.all([
          propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary/page?listAgentMlsId=02060851&daysonmarketSort=newold&firstPhoto=true&hasMedia=true&state=${SITE_CONFIG.STATE}`, {}),
          fetch(`https://tophamtechbe.com/api/treas/private/v1/listings?site_id=${SITE_CONFIG.SITE_ID}`, {
            headers: { 'x-api-key': 'treas-api-key-2025' }
          }),
          propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary/page?city=Westminster&state=${SITE_CONFIG.STATE}&priceSort=highlow&firstPhoto=true&hasMedia=true&pageSize=20`, {})
        ]);

        let mlsProperties = [];
        if (mlsResponse.ok) {
          const data = await mlsResponse.json();
          mlsProperties = data.properties || [];
        }

        let privateListings = [];
        if (privateResponse.ok) {
          const data = await privateResponse.json();
          const listings = data.listings || [];
          privateListings = await Promise.all(listings.map(async l => {
            let photoUrl = null;
            try {
              const mediaRes = await fetch(`https://tophamtechbe.com/api/treas/media/private/v1/media/${SITE_CONFIG.SITE_ID}/${l.id}`, {
                headers: { 'x-api-key': 'treas-api-key-2025' }
              });
              if (mediaRes.ok) {
                const mediaData = await mediaRes.json();
                const first = (mediaData.media || []).sort((a, b) => a.order_hint - b.order_hint)[0];
                photoUrl = first?.s3_link || first?.media_url || null;
              }
            } catch {}
            return {
              listing_key: `PRIVATE_${l.id}`,
              current_price: l.listing_type === 'sale' ? l.price_sale : l.price_lease_monthly,
              street_number: '',
              street_name: l.address_line_1 || '',
              city: l.city || '',
              first_photo_url: photoUrl,
              isPrivate: true
            };
          }));
        }

        let fallbackProperties = [];
        if (fallbackResponse.ok) {
          const data = await fallbackResponse.json();
          fallbackProperties = data.properties || [];
        }

        // Priority: MLS agent listings first, then private, then Westminster fallback
        const combined = [...mlsProperties, ...privateListings];
        const mlsAndPrivateKeys = new Set(combined.map(p => p.listing_key));
        const uniqueFallback = fallbackProperties.filter(p => !mlsAndPrivateKeys.has(p.listing_key));

        let final = [...combined, ...uniqueFallback];
        if (final.length > 20) final = final.slice(0, 20);

        // Fetch media_url for MLS properties missing first_photo_url
        const withMedia = await Promise.all(final.map(async p => {
          if (!p.isPrivate && !p.first_photo_url && p.listing_key) {
            try {
              const mediaRes = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {});
              if (mediaRes.ok) {
                const mediaData = await mediaRes.json();
                const first = (mediaData.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                return { ...p, first_photo_url: first?.media_url || first?.s3_link || null };
              }
            } catch {}
          }
          return p;
        }));

        setFeaturedProperties(withMedia);
      } catch (error) {
        console.error('Error fetching featured properties:', error);
      }
    };

    fetchFeaturedProperties();

    // Handle direct listing access
    if (directListing && listingKey) {
      // Store listing key for ActionButtons to use
      sessionStorage.setItem('directListingKey', listingKey);
    }

    // Track page view
    const trackPageView = async () => {
      try {
        await fetch('https://tophamtechbeb.com/aw-monitor/v1/page-views', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            site_id: SITE_CONFIG.SITE_ID,
            page_path: window.location.pathname,
            referrer: document.referrer || '',
            is_mobile: /Mobi|Android/i.test(navigator.userAgent),
            user_agent: navigator.userAgent,
            session_id: sessionStorage.getItem('session_id') || Math.random().toString(36).substr(2, 9)
          })
        });
      } catch (error) {
        console.error('Error tracking page view:', error);
      }
    };

    if (!sessionStorage.getItem('session_id')) {
      sessionStorage.setItem('session_id', Math.random().toString(36).substr(2, 9));
    }

    trackPageView();

    // Listen for globe map open event from AgentInfo
    const handleOpenGlobeMap = () => {
      setShowGlobeMap(true);
    };

    window.addEventListener('openGlobeMap', handleOpenGlobeMap);

    return () => {
      window.removeEventListener('openGlobeMap', handleOpenGlobeMap);
    };
  }, []);

  const handleToggle = () => {
    console.log('Toggle clicked, current mode:', isDarkMode);
    toggleTheme();
  };

  const handleSignInToggle = () => {
    setShowSignInDropdown(!showSignInDropdown);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/auth/signin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: signInUsername,
          password: signInPassword,
          siteId: SITE_CONFIG.SITE_ID
        })
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAdmin(userData.roles?.includes('ROLE_ADMIN') || false);
        localStorage.setItem('user', JSON.stringify(userData));
        setShowSignInDropdown(false);
        setShowForgotPassword(false);
        setSignInUsername('');
        setSignInPassword('');
        setResetEmail('');
      } else {
        alert('Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...registerData,
          siteId: SITE_CONFIG.SITE_ID
        })
      });

      if (response.ok) {
        // Auto sign in after registration
        const signInResponse = await fetch('https://tophamtechbe.com/api/treas/auth/signin', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: registerData.username,
            password: registerData.password,
            siteId: SITE_CONFIG.SITE_ID
          })
        });

        if (signInResponse.ok) {
          const userData = await signInResponse.json();
          setUser(userData);
          setIsAdmin(userData.roles?.includes('ROLE_ADMIN') || false);
          localStorage.setItem('user', JSON.stringify(userData));
          setShowSignInDropdown(false);
          setRegisterData({ username: '', email: '', password: '', phoneNumber: '', name: '' });
        }
      } else {
        alert('Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      alert('Registration failed. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  const handleLogOut = () => {
    setUser(null);
    setIsAdmin(false);
    setShowSignInDropdown(false);
    localStorage.removeItem('user');
  };

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/auth/password-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: resetEmail,
          baseUrl: window.location.origin,
          siteId: SITE_CONFIG.SITE_ID
        })
      });

      if (response.ok) {
        setResetSuccess(true);
      } else {
        alert('Failed to send reset link. Please try again.');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      alert('Failed to send reset link. Please try again.');
    }
  };

  const handleHomeValueSubmit = async (e) => {
    e.preventDefault();
    if (isHoneypotSubmission(e.currentTarget)) {
      setShowHomeValueModal(false);
      setHomeValueForm({ name: '', email: '', phoneNumber: '', postalCode: '', smsConsent: false});
      setShowConnectSuccess(true);
      setTimeout(() => setShowConnectSuccess(false), 5000);
      return;
    }

    setSubmittingHomeValue(true);
    try {
      const params = new URLSearchParams({
        postalCode: homeValueForm.postalCode,
        name: homeValueForm.name,
        phoneNumber: homeValueForm.phoneNumber,
        smsConsent: homeValueForm.smsConsent ? 'true' : 'false',
        serviceReq: 'Selling',
        email: homeValueForm.email,
        siteId: SITE_CONFIG.SITE_ID
      });
      const response = await submitChatLead(new URL(`https://tophamtechbe.com/api/treas/idx/v1/chatleads?${params}`).searchParams, { rememberContact: true });
      if (response.ok) {
        setShowHomeValueModal(false);
        setHomeValueForm({ name: '', email: '', phoneNumber: '', postalCode: '', smsConsent: false});
        setShowConnectSuccess(true);
        setTimeout(() => setShowConnectSuccess(false), 5000);
      } else {
        alert('Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Home value form error:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmittingHomeValue(false);
    }
  };

  const handleConnectSubmit = async (e) => {
    e.preventDefault();
    if (isHoneypotSubmission(e.currentTarget)) {
      setShowConnectModal(false);
      setConnectForm({ name: '', email: '', phoneNumber: '', postalCode: '', serviceReq: 'buying', smsConsent: false});
      setShowConnectSuccess(true);
      setTimeout(() => setShowConnectSuccess(false), 5000);
      return;
    }

    setSubmittingConnect(true);
    try {
      const params = new URLSearchParams({
        postalCode: connectForm.postalCode,
        name: connectForm.name,
        phoneNumber: connectForm.phoneNumber,
        smsConsent: connectForm.smsConsent ? 'true' : 'false',
        serviceReq: connectForm.serviceReq,
        email: connectForm.email,
        siteId: SITE_CONFIG.SITE_ID
      });

      const response = await submitChatLead(new URL(`https://tophamtechbe.com/api/treas/idx/v1/chatleads?${params}`).searchParams, { rememberContact: true });

      if (response.ok) {
        setShowConnectModal(false);
        setShowConnectSuccess(true);
        setConnectForm({ name: '', email: '', phoneNumber: '', postalCode: '', serviceReq: 'buying', smsConsent: false});
        setTimeout(() => setShowConnectSuccess(false), 5000);
      } else {
        alert('Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Connect form error:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmittingConnect(false);
    }
  };

  return (
    <div className="landing-page">
      <SEOHelmet />
      {/* Hidden content for SEO crawlers */}
      <div style={{position: 'absolute', left: '-9999px', top: '-9999px'}}>
        <h1>Truc Doan — Real Estate Agent in Westminster, CA | Orange County Homes</h1>
        <p>Truc Doan is a licensed California real estate agent with VUI GROUP, serving Westminster, Little Saigon, and the greater Orange County, Los Angeles County, and Riverside County areas. Truc helps buyers, sellers, and renters with expert local guidance and bilingual English and Vietnamese service. Ask the AI assistant a question and get instant answers about listings, neighborhoods, and the buying or selling process.</p>
        <h2>Real Estate Services in Orange County</h2>
        <ul>
          <li>Home buying — find the right home across Westminster, Garden Grove, Fountain Valley, and Orange County</li>
          <li>Home selling — price, market, and sell your property for the best result</li>
          <li>Rentals and leasing — find or lease the right property</li>
          <li>Bilingual service — real estate guidance in English and Vietnamese</li>
          <li>Property search — conversational AI search and personalized recommendations</li>
        </ul>
        <h2>Why Work With Truc Doan?</h2>
        <p>Local expertise in Westminster and Orange County, personalized bilingual service, and an AI-powered assistant that answers your real estate questions instantly. Contact Truc Doan of VUI GROUP at (818) 818-0989 or doanestate@gmail.com.</p>
      </div>

      <FleetLandingDock hidden={showBlogs || showReviews || showGlobeMap || showMapPage} isDarkMode={isDarkMode} onSearch={() => {sessionStorage.removeItem('globeMapData');window.history.replaceState(window.history.state,'','/search');setShowGlobeMap(true);}} onChat={() => navigate('/chat')} onJournal={() => navigate('/blogs')} onReviews={() => navigate('/reviews')} onCloseAccount={() => setShowSignInDropdown(false)} isAdmin={isAdmin} onAdmin={() => { window.location.href = '/admin'; }}><div className="signin-dropdown"><button type="button" className="fleet-dock-button signin-toggle" onClick={handleSignInToggle} aria-expanded={showSignInDropdown} aria-haspopup="dialog"><LandingNavIcon name="account" /><span>{user ? 'Account' : 'Sign in'}</span></button>{showSignInDropdown && (
            <div className="signin-dropdown-content" role="dialog" aria-label={user ? 'Account menu' : 'Sign in'}>
              {!user ? (
                showForgotPassword ? (
                  resetSuccess ? (
                    <div style={{textAlign: 'center', padding: '20px'}}>
                      <h3 style={{color: '#28a745', margin: '0 0 15px 0'}}>{t('auth.emailSent')}</h3>
                      <p style={{margin: '0 0 20px 0', fontSize: '14px', color: '#666'}}>{t('auth.checkEmail')}</p>
                      <button 
                        onClick={() => { setShowForgotPassword(false); setResetEmail(''); setResetSuccess(false); }}
                        className="toggle-form-btn"
                      >
                        {t('auth.backToSignIn')}
                      </button>
                    </div>
                  ) : (
                    <div>
                      <form onSubmit={handlePasswordResetSubmit} className="signin-form">
                        <h3>{t('auth.resetPassword')}</h3>
                        <input
                          type="email"
                          placeholder={t('auth.placeholders.enterEmail')}
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          required
                        />
                        <button type="submit">
                          {t('auth.sendResetLink')}
                        </button>
                      </form>
                      <button 
                        onClick={() => { setShowForgotPassword(false); setResetEmail(''); }}
                        className="toggle-form-btn"
                      >
                        {t('auth.backToSignIn')}
                      </button>
                    </div>
                  )
                ) : !showRegisterForm ? (
                  <div>
                    <form onSubmit={handleSignIn} className="signin-form">
                      <h3>{t('auth.signIn')}</h3>
                      <input
                        type="text"
                        placeholder={t('auth.placeholders.usernameOrEmail')}
                        value={signInUsername}
                        onChange={(e) => setSignInUsername(e.target.value)}
                        required
                      />
                      <input
                        type="password"
                        placeholder={t('auth.placeholders.password')}
                        value={signInPassword}
                        onChange={(e) => setSignInPassword(e.target.value)}
                        required
                      />
                      <button type="submit" disabled={signingIn}>
                        {signingIn ? t('auth.signingIn') : t('auth.signIn')}
                      </button>
                    </form>
                    <button 
                      onClick={() => setShowRegisterForm(true)}
                      className="toggle-form-btn"
                    >
                      {t('auth.needAccount')}
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setShowForgotPassword(true)}
                      style={{background: 'none', border: 'none', color: '#666', fontSize: '11px', textDecoration: 'underline', cursor: 'pointer', marginTop: '12px', padding: '4px 0'}}
                    >
                      {t('auth.forgotPassword')}
                    </button>
                  </div>
                ) : (
                  <div>
                    <form onSubmit={handleRegister} className="signin-form">
                      <h3>{t('auth.register')}</h3>
                      <input
                        type="text"
                        placeholder={t('auth.placeholders.username')}
                        value={registerData.username}
                        onChange={(e) => setRegisterData({...registerData, username: e.target.value})}
                        required
                      />
                      <input
                        type="email"
                        placeholder={t('form.email')}
                        value={registerData.email}
                        onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                        required
                      />
                      <input
                        type="password"
                        placeholder={t('auth.placeholders.password')}
                        value={registerData.password}
                        onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                        required
                      />
                      <input
                        type="tel"
                        placeholder={t('form.phoneNumber')}
                        value={registerData.phoneNumber}
                        onChange={(e) => setRegisterData({...registerData, phoneNumber: e.target.value})}
                        required
                      />
                      <input
                        type="text"
                        placeholder={t('form.fullName')}
                        value={registerData.name}
                        onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                        required
                      />
                      <button type="submit" disabled={signingIn}>
                        {signingIn ? t('auth.registering') : t('auth.register')}
                      </button>
                    </form>
                    <button 
                      onClick={() => setShowRegisterForm(false)}
                      className="toggle-form-btn"
                    >
                      {t('auth.haveAccount')}
                    </button>
                  </div>
                )
              ) : (
                <div className="user-info">
                  <h3>{t('auth.userInfo')}</h3>
                  <p><strong>{t('auth.name')}:</strong> {user.username}</p>
                  <p><strong>{t('auth.email')}:</strong> {user.email}</p>
                  <p><strong>{t('auth.role')}:</strong> {user.roles?.join(', ')}</p>
                  <button onClick={handleLogOut} className="logout-btn">
                    {t('auth.logOff')}
                  </button>
                </div>
              )}
            </div>
          )}</div></FleetLandingDock>
      <div className="fullscreen-chat-container">
        {showIntroVideo && (
          <div className="intro-video-overlay">
{!showGlobeMap && !showMapPage && <FleetLandingHeader onAbout={() => navigate('/agent')}  onContact={() => setShowConnectModal(true)} onToggleTheme={handleToggle} isDarkMode={isDarkMode} />}
            <video 
              ref={videoRef}
              className="intro-video"
              autoPlay 
              muted 
              playsInline
            >
              <source src={
                (/iPad/i.test(navigator.userAgent) || 
                 (window.innerWidth >= 768 && window.innerWidth <= 1024 && 'ontouchstart' in window)) ? "https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/_t.doan.landing.can.002.ipad.mp4" :
                /Mobi|Android/i.test(navigator.userAgent) ? "https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/t.doan.landing.can.002.mobile.mp4" : 
                "https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/t.doan.landing.can.002.mp4"
              } type="video/mp4" />
            </video>
            <div 
              ref={reviewCarouselRef}
              className="review-carousel-container"
              onMouseEnter={() => setIsOverReviewCard(true)}
              onMouseLeave={() => setIsOverReviewCard(false)}
            >
              <div className="review-card">
                <p className="review-text">"{t('landing.review1')}"</p>
                <p className="review-author">- Peter Nguyen</p>
              </div>
              <div className="review-card">
                <p className="review-text">"{t('landing.review2')}"</p>
                <p className="review-author">- Thanh_richard Nguyen</p>
              </div>
              <div className="review-card">
                <p className="review-text">"{t('landing.review3')}"</p>
                <p className="review-author">- Phan Diu</p>
              </div>
              <div className="review-card">
                <p className="review-text">"{t('landing.review4')}"</p>
                <p className="review-author">- Kelly Huynh</p>
              </div>
              <div className="review-card">
                <p className="review-text">"{t('landing.review5')}"</p>
                <p className="review-author">- David Huynh</p>
              </div>
              <div className="review-card">
                <p className="review-text">"{t('landing.review6')}"</p>
                <p className="review-author">- Phuong Nguyen</p>
              </div>
            </div>
          </div>
        )}
        {showIntroVideo && (
          <>
            <div className="landing-action-buttons">
              <button 
                className="landing-action-btn contact-btn"
                onClick={() => setShowConnectModal(true)}
              >
                {t('landing.contactUs')}
              </button>
              <button 
                className="landing-action-btn ai-btn"
                onClick={() => navigate('/chat')}
              >
                {t('landing.letsGetStarted')}
              </button>
            </div>
            <div className="image-page">
              {showImagePageText && (
                <>
                  <div className="image-page-text">
                    <h1>{t('landing.findYourHome')}</h1>
                    <h2>{t('landing.withAgent')}</h2>
                    <p>{t('landing.heroDescription')}</p>
                  </div>
                  <div className="featured-properties-widget">
                    <h3>{t('landing.featuredProperties')}</h3>
                    <div className="mobile-search-link">
                      <button onClick={() => {sessionStorage.removeItem('globeMapData');window.history.replaceState(window.history.state,'','/search');setShowGlobeMap(true);}}>{t('landing.viewFeaturedProperties')}</button>
                    </div>
                    {featuredProperties.length > 0 && (
                      <>
                        <div className="property-carousel">
                          {featuredProperties.map((property, index) => {
                            const offset = index - currentPropertyIndex;
                            const isActive = index === currentPropertyIndex;
                            return (
                              <div 
                                key={index} 
                                className={`featured-property-card ${isActive ? 'active' : ''}`}
                                style={{
                                  transform: `translateX(${offset * 100}%)`,
                                  opacity: isActive ? 1 : 0.3,
                                  pointerEvents: isActive ? 'auto' : 'none'
                                }}
                                onClick={() => {
                                  if (isActive) {
                                    setShowIntroVideo(false);
                                    navigate(`/listing/${property.listing_key}`);
                                  }
                                }}
                              >
                                <img src={property.first_photo_url} alt={formatStreetAddress(property)} />
                                <div className="property-info">
                                  <p className="property-price">${property.current_price?.toLocaleString()}</p>
                                  <p className="property-address">{formatPropertyAddress(property)}</p>
                                  {(property.list_agent_full_name || property.list_office_name) && (
                                    <p style={{fontSize: '11px', color: '#ddd', margin: '2px 0 0'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="property-nav-buttons">
                          <button onClick={(e) => { e.stopPropagation(); setCurrentPropertyIndex((prev) => (prev - 1 + featuredProperties.length) % featuredProperties.length); }}>←</button>
                          <span>{currentPropertyIndex + 1} / {featuredProperties.length}</span>
                          <button onClick={(e) => { e.stopPropagation(); setCurrentPropertyIndex((prev) => (prev + 1) % featuredProperties.length); }}>→</button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="e-image-page">
              {showEPage && (
                <>
                  <h2 className="e-image-heading">{t('landing.readyForEvaluation')}</h2>
                  <button className="home-value-btn" onClick={() => setShowHomeValueModal(true)}>{t('landing.freeHomeValue')}</button>
                </>
              )}
            </div>
            <div className="city-tiles-page" style={{ backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/demo-landing-C.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
            {showCityTiles && (
              <>
                <h2 className="city-tiles-title">{t('landing.exploreServiceAreas')}</h2>
                <div className="city-tiles-grid">
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/tulsahome_tile.jpg)', backgroundPosition: 'center center', backgroundSize: '110%'}} onClick={() => navigate('/orange-county')}>{t('county.orangeCounty')}</div>
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/bixby_landing_tile.jpeg)', backgroundPosition: 'center center', backgroundSize: '110%'}} onClick={() => navigate('/los-angeles-county')}>{t('county.losAngelesCounty')}</div>
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/jenks_landing_tile.jpeg)', backgroundPosition: 'center center', backgroundSize: '110%'}} onClick={() => navigate('/riverside-county')}>{t('county.riversideCounty')}</div>
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/brokenarrow_landing_tile.jpeg)', backgroundPosition: 'center center', backgroundSize: '110%'}} onClick={() => navigate('/san-bernardino-county')}>{t('county.sanBernardinoCounty')}</div>
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/owasso_landing_tile.webp)', backgroundPosition: 'center center', backgroundSize: '110%'}} onClick={() => navigate('/ventura-county')}>{t('county.venturaCounty')}</div>
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic_sandiego_001.jpeg)', backgroundPosition: 'center center', backgroundSize: '110%'}} onClick={() => navigate('/san-diego-county')}>{t('county.sanDiegoCounty')}</div>
                </div>
              </>
            )}
          </div>
          </>
        )}
        <div className={`chat-wrapper ${!showIntroVideo ? 'fullscreen' : 'hidden'}`}>
          <ActionButtons 
            activeSection={activeSection} 
            setActiveSection={setActiveSection}
            user={user}
            isAdmin={isAdmin}
            setUser={setUser}
            setIsAdmin={setIsAdmin}
            hidePrompts={!showIntroVideo}
          />
        </div>
      </div>

      {showMapPage && (
        <div className="full-map-page">
          <button 
            className="close-map-page"
            onClick={() => setShowMapPage(false)}
          >
            {t('landing.backToChat')}
          </button>
          <ActionButtons 
            activeSection={activeSection} 
            setActiveSection={setActiveSection}
            isMapPage={true}
            onMapPageClose={() => setShowMapPage(false)}
            user={user}
            isAdmin={isAdmin}
            setUser={setUser}
            setIsAdmin={setIsAdmin}
          />
        </div>
      )}
      {showGlobeMap && (
        <div className="full-map-page">
          <ActionButtons 
            activeSection={activeSection} 
            setActiveSection={setActiveSection}
            isGlobeMap={true}
            onGlobeMapResultsChange={setShowGlobeMapResults}
            onGlobeMapClose={() => {sessionStorage.removeItem('globeMapData');window.history.replaceState(window.history.state,'','/');
              setShowGlobeMap(false);
              setShowGlobeMapResults(false);
              setShowIntroVideo(true);
            }}
            directListingKey={directListing ? listingKey : null}
            user={user}
            isAdmin={isAdmin}
            setUser={setUser}
            setIsAdmin={setIsAdmin}
          />
        </div>
      )}
      {showBlogs && (
        <BlogsPage onClose={() => setShowBlogs(false)} />
      )}
      {showReviews && (
        <ReviewsPage onClose={() => setShowReviews(false)} />
      )}

      <footer className={`landing-footer ${!showIntroVideo ? 'hidden' : ''}`}>
        <a href="/privacy-policy" className="footer-link">{t('footer.privacyPolicy')}</a>
        <span className="footer-separator"> | </span>
        <a href="/terms-of-service" className="footer-link">{t('footer.termsOfService')}</a>
        <span className="footer-separator"> | </span>
        <button 
          onClick={() => setShowMLSDisclosure(true)}
          className="footer-link mls-disclosure-link"
        >
          {t('footer.mlsDisclosure')}
        </button>
        <span className="footer-separator-logo"> | </span>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{height: '24px', objectFit: 'contain', verticalAlign: 'middle'}} />
        <span className="footer-separator-logo"> | </span>
        <a href="https://agentwinds.com" target="_blank" rel="noopener noreferrer">
          <img src="https://aw-business-marketing.s3.us-east-1.amazonaws.com/websitesbyaw-transparent.png" alt="Websites by AW" className="footer-logo" />
        </a>
      </footer>

      {showMLSDisclosure && (
        <div className="modal-overlay" onClick={() => setShowMLSDisclosure(false)}>
          <div className="mls-disclosure-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('footer.mlsDisclosure')}</h3>
            <p>
              {t('footer.mlsDisclosureText', { date: new Date().toLocaleDateString() })}
            </p>
            <button onClick={() => setShowMLSDisclosure(false)} className="close-disclosure-btn">
              {t('footer.close')}
            </button>
          </div>
        </div>
      )}

      {showConnectSuccess && (
        <div className="connect-success-notification">
          <div className="connect-success-content">
            <span className="connect-success-icon">✓</span>
            <span className="connect-success-text">{t('landing.thankYou')}</span>
          </div>
        </div>
      )}

      {showHomeValueModal && (
        <div className="modal-overlay" onClick={() => setShowHomeValueModal(false)}>
          <div className="connect-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('landing.freeHomeValue')}</h3>
            <form onSubmit={handleHomeValueSubmit}>
          <HoneypotField />
              <input type="text" placeholder={t('form.fullName')} value={homeValueForm.name} onChange={(e) => setHomeValueForm({...homeValueForm, name: e.target.value})} required />
              <input type="email" placeholder={t('form.email')} value={homeValueForm.email} onChange={(e) => setHomeValueForm({...homeValueForm, email: e.target.value})} required />
              <input type="tel" placeholder={t('form.phoneNumber')} value={homeValueForm.phoneNumber} onChange={(e) => setHomeValueForm({...homeValueForm, phoneNumber: e.target.value})} required />
              <input type="text" placeholder={t('form.zipCode')} value={homeValueForm.postalCode} onChange={(e) => setHomeValueForm({...homeValueForm, postalCode: e.target.value})} required />
              <label className="sms-consent-control">
                <input type="checkbox" checked={homeValueForm.smsConsent} onChange={(e) => setHomeValueForm({...homeValueForm, smsConsent: e.target.checked})} />
                <span>I agree to receive text messages about this request. Consent is not a condition of service. Message and data rates may apply; reply STOP to opt out.</span>
              </label>
              <button type="submit" disabled={submittingHomeValue}>{submittingHomeValue ? t('form.submitting') : t('form.submit')}</button>
              <button type="button" onClick={() => setShowHomeValueModal(false)} className="cancel-btn">{t('form.cancel')}</button>
            </form>
          </div>
        </div>
      )}

      {showConnectModal && (
        <div className="modal-overlay" onClick={() => setShowConnectModal(false)}>
          <div className="connect-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('landing.readyToConnect')}</h3>
            <form onSubmit={handleConnectSubmit}>
          <HoneypotField />
              <input
                type="text"
                placeholder={t('form.fullName')}
                value={connectForm.name}
                onChange={(e) => setConnectForm({...connectForm, name: e.target.value})}
                required
              />
              <input
                type="email"
                placeholder={t('form.email')}
                value={connectForm.email}
                onChange={(e) => setConnectForm({...connectForm, email: e.target.value})}
                required
              />
              <input
                type="tel"
                placeholder={t('form.phoneNumber')}
                value={connectForm.phoneNumber}
                onChange={(e) => setConnectForm({...connectForm, phoneNumber: e.target.value})}
                required
              />
              <input
                type="text"
                placeholder={t('form.zipCode')}
                value={connectForm.postalCode}
                onChange={(e) => setConnectForm({...connectForm, postalCode: e.target.value})}
                required
              />
              <label className="sms-consent-control">
                <input type="checkbox" checked={connectForm.smsConsent} onChange={(e) => setConnectForm({...connectForm, smsConsent: e.target.checked})} />
                <span>I agree to receive text messages about this request. Consent is not a condition of service. Message and data rates may apply; reply STOP to opt out.</span>
              </label>
              <select
                value={connectForm.serviceReq}
                onChange={(e) => setConnectForm({...connectForm, serviceReq: e.target.value})}
                required
              >
                <option value="buying">{t('form.buying')}</option>
                <option value="selling">{t('form.selling')}</option>
                <option value="both">{t('form.both')}</option>
              </select>
              <button type="submit" disabled={submittingConnect}>
                {submittingConnect ? t('form.submitting') : t('form.submit')}
              </button>
              <button type="button" onClick={() => setShowConnectModal(false)} className="cancel-btn">
                {t('form.cancel')}
              </button>
            </form>
          </div>
        </div>
      )}

      <SEOLinks />
      <LanguageSwitcher />
    </div>
  );
};

export default LandingPage;
